import { requiredStringAndDigitsMax } from "@features/licensing/schema/licenseRequestValidation";
import type { TFunction } from "i18next";
import { z } from "zod";

export const MAX_RECEIPT_NUMBER_LENGTH = 10;
export const MAX_RECEIPT_AMOUNT_DIGITS = 8;
export const MAX_RECEIPT_AMOUNT_DECIMALS = 2;

const RECEIPT_AMOUNT_REGEX = /^\d+(\.\d+)?$/;

const parseLocalDate = (value: string) => new Date(`${value}T00:00:00`);

const getReceiptAmountParts = (value: string) => value.split(".");

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const receiptUploadSchema = (t: TFunction) =>
  z.object({
    receiptNumber: requiredStringAndDigitsMax(
      MAX_RECEIPT_NUMBER_LENGTH,
      t("licensing.receipt-number"),
      t,
    ),
    receiptDate: z
      .string()
      .min(
        1,
        t("licensing.form-validation.mandatory", {
          label: t("licensing.receipt-date"),
        }),
      )
      .refine(
        (v) => {
          const parsed = parseLocalDate(v);
          return !Number.isNaN(parsed.getTime()) && parsed <= startOfToday();
        },
        {
          message: t("licensing.form-validation.date-not-in-future", {
            label: t("licensing.receipt-date"),
          }),
        },
      ),
    receiptAmount: z
      .string()
      .trim()
      .min(
        1,
        t("licensing.form-validation.mandatory", {
          label: t("licensing.receipt-amount"),
        }),
      )
      .regex(RECEIPT_AMOUNT_REGEX, {
        message: t("licensing.form-validation.receipt-amount-invalid", {
          label: t("licensing.receipt-amount"),
          maxDigits: MAX_RECEIPT_AMOUNT_DIGITS,
          maxDecimals: MAX_RECEIPT_AMOUNT_DECIMALS,
        }),
      })
      .refine((value) => getReceiptAmountParts(value)[0].length <= MAX_RECEIPT_AMOUNT_DIGITS, {
        message: t("form-validation.max-length-digits", {
          label: t("licensing.receipt-amount"),
          maxDigits: MAX_RECEIPT_AMOUNT_DIGITS,
        }),
      })
      .refine(
        (value) => {
          const [, decimalPart = ""] = getReceiptAmountParts(value);
          return decimalPart.length <= MAX_RECEIPT_AMOUNT_DECIMALS;
        },
        {
          message: t("form-validation.max-length-decimals", {
            label: t("licensing.receipt-amount"),
            maxDecimals: MAX_RECEIPT_AMOUNT_DECIMALS,
          }),
        },
      ),
  });

export type ReceiptUploadSchema = z.infer<ReturnType<typeof receiptUploadSchema>>;
